/**
 * TC String Encoder/Decoder
 * Implements IAB TCF 2.3 TC String specification (core + disclosedVendors segments)
 * https://github.com/InteractiveAdvertisingBureau/GDPR-Transparency-and-Consent-Framework/blob/master/TCFv2/IAB%20Tech%20Lab%20-%20Consent%20string%20and%20vendor%20list%20formats%20v2.md
 */

import { getMaxVendorId, getAllPurposeIds, getActiveVendorIds, gvlMetadata } from './gvl-data.js';

// Segment types for the segments that follow the core string
const SEGMENT_TYPE_DISCLOSED_VENDORS = 1;

// Base64url alphabet (URL-safe)
const BASE64URL_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/**
 * Convert a binary string to base64url
 */
function binaryToBase64url(binaryString) {
  // Pad to multiple of 6
  while (binaryString.length % 6 !== 0) {
    binaryString += '0';
  }
  
  let result = '';
  for (let i = 0; i < binaryString.length; i += 6) {
    const chunk = binaryString.substring(i, i + 6);
    const index = parseInt(chunk, 2);
    result += BASE64URL_CHARS[index];
  }
  
  return result;
}

/**
 * Convert base64url to binary string
 */
function base64urlToBinary(base64String) {
  let result = '';
  for (let i = 0; i < base64String.length; i++) {
    const char = base64String[i];
    const index = BASE64URL_CHARS.indexOf(char);
    if (index === -1) continue;
    result += index.toString(2).padStart(6, '0');
  }
  return result;
}

/**
 * Encode an integer to binary with specified bit length
 * Uses BigInt for values > 32 bits to avoid JavaScript's 32-bit integer limit
 */
function intToBinary(value, numBits) {
  if (numBits > 32 || value > 0xFFFFFFFF) {
    // Use BigInt for large values (like timestamps)
    return BigInt(value).toString(2).padStart(numBits, '0');
  }
  return (value >>> 0).toString(2).padStart(numBits, '0');
}

/**
 * Encode a boolean to binary (1 bit)
 */
function boolToBinary(value) {
  return value ? '1' : '0';
}

/**
 * Encode a date to binary (deciseconds since epoch, 36 bits)
 * TCF requires timestamps to be "imprecise" - truncated to reduce precision
 * We truncate to the nearest DAY for privacy (TCF 2.2 requirement)
 */
function dateToBinary(date) {
  // Truncate to nearest day (86400000 ms) for imprecision requirement
  const truncatedTime = Math.floor(date.getTime() / 86400000) * 86400000;
  const deciseconds = Math.floor(truncatedTime / 100);
  return intToBinary(deciseconds, 36);
}

/**
 * Encode a bitfield (array of booleans or Set of IDs)
 */
function encodeBitfield(consents, maxId) {
  let bits = '';
  for (let i = 1; i <= maxId; i++) {
    if (consents instanceof Set) {
      bits += consents.has(i) ? '1' : '0';
    } else if (Array.isArray(consents)) {
      bits += consents.includes(i) ? '1' : '0';
    } else {
      bits += consents[i] ? '1' : '0';
    }
  }
  return bits;
}

/**
 * Encode vendor consents as a bitfield or range
 * For simplicity, we use bitfield encoding
 */
function encodeVendorConsents(vendorConsents, maxVendorId) {
  return encodeBitfield(vendorConsents, maxVendorId);
}

/**
 * Generate a TC String from consent data
 * @param {Object} consentData - The consent data object
 * @returns {string} - The encoded TC String
 */
export function encodeTCString(consentData) {
  const {
    cmpId = 481,  // Captain Compliance registered CMP ID
    cmpVersion = 1,
    consentScreen = 1,
    consentLanguage = 'EN',
    vendorListVersion = gvlMetadata.vendorListVersion, // Bundled GVL version
    tcfPolicyVersion = gvlMetadata.tcfPolicyVersion, // Must match GVL tcfPolicyVersion
    isServiceSpecific = true,
    useNonStandardTexts = false,
    purposeOneTreatment = false,
    publisherCC = 'US',
    purposeConsents = new Set(),
    purposeLegitimateInterests = new Set(),
    vendorConsents = new Set(),
    vendorLegitimateInterests = new Set(),
    specialFeatureOptins = new Set(),
    publisherRestrictions = [],
    disclosedVendors = new Set(getActiveVendorIds())
  } = consentData;

  const now = new Date();
  const maxVendorId = getMaxVendorId();
  
  let binary = '';
  
  // Core String Segment
  // Version (6 bits) - TCF v2.2 = version 2
  binary += intToBinary(2, 6);
  
  // Created (36 bits) - deciseconds since epoch
  binary += dateToBinary(now);
  
  // LastUpdated (36 bits)
  binary += dateToBinary(now);
  
  // CmpId (12 bits)
  binary += intToBinary(cmpId, 12);
  
  // CmpVersion (12 bits)
  binary += intToBinary(cmpVersion, 12);
  
  // ConsentScreen (6 bits)
  binary += intToBinary(consentScreen, 6);
  
  // ConsentLanguage (12 bits - 2x6 bit letters)
  const lang = consentLanguage.toUpperCase();
  binary += intToBinary(lang.charCodeAt(0) - 65, 6);
  binary += intToBinary(lang.charCodeAt(1) - 65, 6);
  
  // VendorListVersion (12 bits)
  binary += intToBinary(vendorListVersion, 12);
  
  // TcfPolicyVersion (6 bits)
  binary += intToBinary(tcfPolicyVersion, 6);
  
  // IsServiceSpecific (1 bit)
  binary += boolToBinary(isServiceSpecific);
  
  // UseNonStandardTexts (1 bit)
  binary += boolToBinary(useNonStandardTexts);
  
  // SpecialFeatureOptins (12 bits - bitfield for special features 1-12)
  for (let i = 1; i <= 12; i++) {
    binary += specialFeatureOptins.has(i) ? '1' : '0';
  }
  
  // PurposeConsents (24 bits - bitfield for purposes 1-24)
  for (let i = 1; i <= 24; i++) {
    binary += purposeConsents.has(i) ? '1' : '0';
  }
  
  // PurposeLegitimateInterests (24 bits)
  for (let i = 1; i <= 24; i++) {
    binary += purposeLegitimateInterests.has(i) ? '1' : '0';
  }
  
  // PurposeOneTreatment (1 bit)
  binary += boolToBinary(purposeOneTreatment);
  
  // PublisherCC (12 bits - 2x6 bit letters)
  const cc = publisherCC.toUpperCase();
  binary += intToBinary(cc.charCodeAt(0) - 65, 6);
  binary += intToBinary(cc.charCodeAt(1) - 65, 6);
  
  // MaxVendorId (16 bits)
  binary += intToBinary(maxVendorId, 16);
  
  // IsRangeEncoding (1 bit) - using bitfield (0)
  binary += '0';
  
  // VendorConsents bitfield
  binary += encodeVendorConsents(vendorConsents, maxVendorId);
  
  // MaxVendorIdLI (16 bits)
  binary += intToBinary(maxVendorId, 16);
  
  // IsRangeEncodingLI (1 bit) - using bitfield (0)
  binary += '0';
  
  // VendorLegitimateInterests bitfield
  binary += encodeVendorConsents(vendorLegitimateInterests, maxVendorId);
  
  // NumPubRestrictions (12 bits) - none for simplicity
  binary += intToBinary(0, 12);
  
  // Convert to base64url
  const coreString = binaryToBase64url(binary);
  
  // TCF 2.3: the disclosedVendors segment is mandatory
  return `${coreString}.${encodeDisclosedVendorsSegment(disclosedVendors)}`;
}

/**
 * Encode the disclosedVendors segment: every vendor shown to the user in the CMP UI
 */
function encodeDisclosedVendorsSegment(disclosedVendors) {
  const ids = Array.from(disclosedVendors);
  const maxVendorId = ids.length > 0 ? Math.max(...ids) : 0;
  
  let binary = '';
  
  // SegmentType (3 bits)
  binary += intToBinary(SEGMENT_TYPE_DISCLOSED_VENDORS, 3);
  
  // MaxVendorId (16 bits)
  binary += intToBinary(maxVendorId, 16);
  
  // IsRangeEncoding (1 bit) - using bitfield (0)
  binary += '0';
  
  // Disclosed vendors bitfield
  binary += encodeBitfield(new Set(ids), maxVendorId);
  
  return binaryToBase64url(binary);
}

/**
 * Decode a TC String (partial implementation for validation)
 * @param {string} tcString - The TC String to decode
 * @returns {Object} - Decoded consent data
 */
export function decodeTCString(tcString) {
  try {
    const [coreSegment, ...otherSegments] = tcString.split('.');
    let binary = base64urlToBinary(coreSegment);
    let pos = 0;
    
    const readBits = (numBits) => {
      const bits = binary.substring(pos, pos + numBits);
      pos += numBits;
      return parseInt(bits, 2);
    };
    
    const readBool = () => readBits(1) === 1;
    
    const readDate = () => {
      const deciseconds = readBits(36);
      return new Date(deciseconds * 100);
    };
    
    const readString = (numChars) => {
      let str = '';
      for (let i = 0; i < numChars; i++) {
        str += String.fromCharCode(readBits(6) + 65);
      }
      return str;
    };
    
    const readBitfield = (numBits) => {
      const result = new Set();
      for (let i = 1; i <= numBits; i++) {
        if (readBool()) {
          result.add(i);
        }
      }
      return result;
    };
    
    // Core String
    const version = readBits(6);
    const created = readDate();
    const lastUpdated = readDate();
    const cmpId = readBits(12);
    const cmpVersion = readBits(12);
    const consentScreen = readBits(6);
    const consentLanguage = readString(2);
    const vendorListVersion = readBits(12);
    const tcfPolicyVersion = readBits(6);
    const isServiceSpecific = readBool();
    const useNonStandardTexts = readBool();
    const specialFeatureOptins = readBitfield(12);
    const purposeConsents = readBitfield(24);
    const purposeLegitimateInterests = readBitfield(24);
    const purposeOneTreatment = readBool();
    const publisherCC = readString(2);
    const maxVendorId = readBits(16);
    const isRangeEncoding = readBool();
    
    // Read vendor consents
    const vendorConsents = new Set();
    if (!isRangeEncoding) {
      for (let i = 1; i <= maxVendorId; i++) {
        if (readBool()) {
          vendorConsents.add(i);
        }
      }
    }
    
    // Optional segments after the core string
    let disclosedVendors = null;
    for (const segment of otherSegments) {
      binary = base64urlToBinary(segment);
      pos = 0;
      if (readBits(3) !== SEGMENT_TYPE_DISCLOSED_VENDORS) continue;
      const segmentMaxVendorId = readBits(16);
      if (readBool()) continue; // range encoding is never written by this CMP
      disclosedVendors = readBitfield(segmentMaxVendorId);
    }
    
    return {
      version,
      created,
      lastUpdated,
      cmpId,
      cmpVersion,
      consentScreen,
      consentLanguage,
      vendorListVersion,
      tcfPolicyVersion,
      isServiceSpecific,
      useNonStandardTexts,
      specialFeatureOptins,
      purposeConsents,
      purposeLegitimateInterests,
      purposeOneTreatment,
      publisherCC,
      maxVendorId,
      vendorConsents,
      disclosedVendors
    };
  } catch (e) {
    console.error('Error decoding TC String:', e);
    return null;
  }
}

/**
 * Validate a TC String format
 * @param {string} tcString - The TC String to validate
 * @returns {boolean} - Whether the string is valid
 */
export function isValidTCString(tcString) {
  if (!tcString || typeof tcString !== 'string') {
    return false;
  }
  
  // Check for valid base64url segments joined with '.'
  const validChars = /^[A-Za-z0-9\-_]+(\.[A-Za-z0-9\-_]+)*$/;
  if (!validChars.test(tcString)) {
    return false;
  }
  
  // Try to decode
  const decoded = decodeTCString(tcString);
  if (!decoded) {
    return false;
  }
  
  // Check version
  if (decoded.version !== 2) {
    return false;
  }
  
  return true;
}



